
import React, { useState, useMemo, useEffect } from 'react';
import { UserAccount, Quote, Role } from '../types';
import { frmsService, FRMSMetrics, FRMSDuty } from '../services/frms';
import { userService } from '../services/userService';
import { telemetryApi } from '../services/telemetryApi';
import { reverseGeocodeBatch } from '../services/geocodingApi';
import api from '../services/api';

interface PilotManagerProps {
  users: UserAccount[];
  setUsers: React.Dispatch<React.SetStateAction<UserAccount[]>>;
  quotes: Quote[];
}

const PilotManager: React.FC<PilotManagerProps> = ({ users, setUsers, quotes }) => {
  const [filter, setFilter] = useState('');
  const [frmsData, setFrmsData] = useState<Record<string, FRMSMetrics>>({});
  const [frmsDuties, setFrmsDuties] = useState<Record<string, { today: any[]; tomorrow: any[] }>>({});
  const [frmsPilots, setFrmsPilots] = useState<Record<string, any>>({});
  const [loadingFrms, setLoadingFrms] = useState(false);
  const [pilotTelemetry, setPilotTelemetry] = useState<Record<string, {
    currentBase: string;
    status: 'IN_FLIGHT' | 'STATIONARY' | 'UNKNOWN';
    aircraft?: string;
    location?: string;
    lat?: number;
    lng?: number;
  }>>({});
  const [pilotPlaceNames, setPilotPlaceNames] = useState<Record<string, string>>({});
  const [pilotSectors, setPilotSectors] = useState<Record<string, Array<{
    from: string;
    to: string;
    departureTime: string;
    arrivalTime: string;
    duration: number;
    distance?: number;
  }>>>({});
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  
  const pilots = useMemo(() => {
    if (!Array.isArray(users)) return [];
    // Include all pilot-related roles matching permissions system
    return users.filter(u => {
      const role = (u as any).role || u.role;
      return role === 'CP' || role === 'DFO' || role === 'PILOT' || role === 'FIRST_OFFICER' || role === 'FLIGHT_PLANNER';
    });
  }, [users]);

  // Fetch FRMS data for all pilots
  useEffect(() => {
    const fetchFrmsData = async () => {
      if (pilots.length === 0) return;
      
      setLoadingFrms(true);
      try {
        // First, get all FRMS pilots to match by email
        const allFrmsPilots = await frmsService.getAllPilots();
        const frmsPilotsMap: Record<string, any> = {};
        allFrmsPilots.forEach(p => {
          if (p.email) {
            frmsPilotsMap[p.email.toLowerCase()] = p;
          }
        });
        setFrmsPilots(frmsPilotsMap);

        // Get today and tomorrow dates
        const today = new Date().toISOString().split('T')[0];
        const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const promises = pilots.map(async (pilot) => {
          try {
            const pilotEmail = (pilot as any).email || '';
            const frmsPilot = pilotEmail ? frmsPilotsMap[pilotEmail.toLowerCase()] : null;
            const frmsPilotId = frmsPilot?.id || pilot.id;

            // Fetch metrics, duties, and pilot profile in parallel
            const [metrics, todayDuties, tomorrowDuties] = await Promise.all([
              frmsService.getPilotMetrics(frmsPilotId).catch(() => null),
              frmsService.getPilotDuties(frmsPilotId, today, today).catch(() => []),
              frmsService.getPilotDuties(frmsPilotId, tomorrow, tomorrow).catch(() => []),
            ]);

            // Calculate duty hours today from actual duties
            const dutyHoursToday = (todayDuties as any[]).reduce((sum: number, d: any) => sum + (d.dutyDurationHours || d.displayHours || 0), 0);

            // Merge metrics with pilot profile data
            const enhancedMetrics: FRMSMetrics = {
              ...metrics,
              userId: frmsPilotId,
              currentBase: frmsPilot?.base || metrics?.currentBase || null,
              licenseExpiry: frmsPilot?.compliance?.licenseExpiry || metrics?.licenseExpiry || null,
              medicalExpiry: frmsPilot?.compliance?.medicalExpiry || null,
              irExpiry: frmsPilot?.compliance?.irExpiry || null,
              dutyHoursToday: dutyHoursToday || metrics?.dutyHoursToday || 0,
            };

            return {
              pilotId: pilot.id,
              metrics: enhancedMetrics,
              duties: { today: todayDuties, tomorrow: tomorrowDuties },
            };
          } catch (error: any) {
            console.error(`[PilotManager] Failed to fetch FRMS data for ${(pilot as any).email || pilot.id}:`, error?.message || error);
            return null;
          }
        });

        const results = await Promise.all(promises);
        const dataMap: Record<string, FRMSMetrics> = {};
        const dutiesMap: Record<string, { today: any[]; tomorrow: any[] }> = {};
        
        results.forEach(result => {
          if (result) {
            dataMap[result.pilotId] = result.metrics;
            dutiesMap[result.pilotId] = result.duties;
          }
        });
        
        setFrmsData(dataMap);
        setFrmsDuties(dutiesMap);
      } catch (error: any) {
        console.error('[PilotManager] Failed to fetch FRMS data:', error?.message || error);
      } finally {
        setLoadingFrms(false);
      }
    };

    fetchFrmsData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchFrmsData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [pilots]);

  // Fetch v2Track telemetry data for pilots
  useEffect(() => {
    let isMounted = true;
    const FETCH_TIMEOUT_MS = 12000; // Whole fetch must finish or we show N/A

    const fetchPilotTelemetry = async () => {
      if (pilots.length === 0) {
        setLoadingTelemetry(false);
        return;
      }

      setLoadingTelemetry(true);
      try {
        const run = async () => {
          // Get both active and recently completed missions to find pilot assignments and last known location
          const [activeMissionsResponse, completedMissionsResponse] = await Promise.all([
            Promise.race([
              api.get('/missions', { params: { status: 'IN_PROGRESS', limit: 50 } }).catch(() => ({ data: { data: [] } })),
              new Promise<any>((resolve) => setTimeout(() => resolve({ data: { data: [] } }), 5000))
            ]),
            Promise.race([
              api.get('/missions', { params: { status: 'COMPLETED', limit: 20 } }).catch(() => ({ data: { data: [] } })),
              new Promise<any>((resolve) => setTimeout(() => resolve({ data: { data: [] } }), 5000))
            ])
          ]);

          const activeMissions = activeMissionsResponse.data?.data || [];
          const completedMissions = completedMissionsResponse.data?.data || [];
          const allMissions = [...activeMissions, ...completedMissions];

          // Get telemetry snapshot with timeout so we never hang
          const telemetrySnapshot = await Promise.race([
            telemetryApi.getSnapshot().catch(() => ({})),
            new Promise<Record<string, never>>((resolve) => setTimeout(() => resolve({}), 5000))
          ]);

          // Get aircraft details to access lastLandedLocation
          const aircraftResponse = await Promise.race([
            api.get('/fleet/aircrafts').catch(() => ({ data: [] })),
            new Promise<any>((resolve) => setTimeout(() => resolve({ data: [] }), 3000))
          ]);
          const rawFleet = aircraftResponse.data;
          const aircraftList = Array.isArray(rawFleet) ? rawFleet : Array.isArray((rawFleet as any)?.data) ? (rawFleet as any).data : [];
          const aircraftMap = new Map(aircraftList.map((ac: any) => [ac.tailNumber, ac]));

          const pilotData: Record<string, {
          currentBase: string;
          status: 'IN_FLIGHT' | 'STATIONARY' | 'UNKNOWN';
          aircraft?: string;
          location?: string;
          lat?: number;
          lng?: number;
        }> = {};
        
        const sectorsData: Record<string, Array<{
          from: string;
          to: string;
          departureTime: string;
          arrivalTime: string;
          duration: number;
          distance?: number;
        }>> = {};

        // For each pilot, find their assigned aircraft
        const pilotPromises = pilots.map(async (pilot) => {
          const pilotId = pilot.id;
          const pilotInfo: {
            currentBase: string;
            status: 'IN_FLIGHT' | 'STATIONARY' | 'UNKNOWN';
            aircraft?: string;
            location?: string;
            lat?: number;
            lng?: number;
          } = {
            currentBase: 'N/A',
            status: 'UNKNOWN',
          };
          const sectors: Array<{
            from: string;
            to: string;
            departureTime: string;
            arrivalTime: string;
            duration: number;
            distance?: number;
          }> = [];

          // Track last known location from completed missions
          let lastKnownLocation: { base: string; aircraft?: string; time?: Date } | null = null;
          
          // Track all aircraft this pilot has been on (for telemetry lookup)
          const pilotAircraft = new Set<string>();
          
          // First, find active legs where this pilot is assigned (priority)
          for (const mission of allMissions) {
            if (!mission.Leg || !Array.isArray(mission.Leg)) continue;
            
            for (const leg of mission.Leg) {
              // Check if pilot is assigned to this leg
              const crewAssignments = leg.CrewAssignment || [];
              const pilotAssignment = crewAssignments.find((ca: any) => 
                ca.userId === pilotId || ca.User?.id === pilotId
              );
              
              if (!pilotAssignment || !leg.aircraftId || !leg.Aircraft) continue;
              
              const aircraft = leg.Aircraft;
              const tailNumber = aircraft.tailNumber;
              const telemetry = telemetrySnapshot[tailNumber];
              const aircraftDetails = aircraftMap.get(tailNumber) as any;
              
              // Track this aircraft for this pilot
              pilotAircraft.add(tailNumber);
              
              // Track last known location from completed legs (check all, not just today)
              if (leg.status === 'COMPLETED' && (leg.ata || leg.actualEta)) {
                const arrivalTime = new Date(leg.ata || leg.actualEta || leg.eta);
                // Check if this is more recent than what we have
                if (!lastKnownLocation || arrivalTime > (lastKnownLocation.time || new Date(0))) {
                  // Prefer aircraft's lastLandedLocation, then leg arrival, then departure
                  const base = aircraftDetails?.lastLandedLocation || 
                              leg.arrivalICAO || leg.to || 
                              leg.departureICAO || leg.from || 
                              'N/A';
                  lastKnownLocation = {
                    base: base,
                    aircraft: tailNumber,
                    time: arrivalTime,
                  };
                }
              }
              
              // For active/in-flight legs, set current status
              if (leg.status === 'IN_FLIGHT' || leg.status === 'SCHEDULED') {
                if (telemetry) {
                  const speed = telemetry.groundSpeed || 0;
                  const isInFlight = speed > 30;
                  
                  // Determine current base/status
                  if (isInFlight) {
                    pilotInfo.currentBase = `IN FLIGHT → ${leg.arrivalICAO || leg.to || 'N/A'}`;
                    pilotInfo.status = 'IN_FLIGHT';
                    pilotInfo.aircraft = tailNumber;
                    pilotInfo.location = `${telemetry.lat.toFixed(4)}, ${telemetry.lng.toFixed(4)}`;
                    pilotInfo.lat = telemetry.lat;
                    pilotInfo.lng = telemetry.lng;
                  } else {
                    // Stationary - v2Track position; currentBase may be overridden by geocoded place
                    const lastLocation = leg.arrivalICAO || leg.to || leg.departureICAO || leg.from || 'N/A';
                    pilotInfo.currentBase = lastLocation;
                    pilotInfo.status = 'STATIONARY';
                    pilotInfo.aircraft = tailNumber;
                    pilotInfo.location = `${telemetry.lat.toFixed(4)}, ${telemetry.lng.toFixed(4)}`;
                    pilotInfo.lat = telemetry.lat;
                    pilotInfo.lng = telemetry.lng;
                  }
                } else {
                  // No telemetry, use leg information
                  if (leg.status === 'IN_FLIGHT') {
                    pilotInfo.currentBase = `IN FLIGHT → ${leg.arrivalICAO || leg.to || 'N/A'}`;
                    pilotInfo.status = 'IN_FLIGHT';
                    pilotInfo.aircraft = tailNumber;
                  } else {
                    pilotInfo.currentBase = leg.arrivalICAO || leg.to || leg.departureICAO || leg.from || 'N/A';
                    pilotInfo.status = 'STATIONARY';
                    pilotInfo.aircraft = tailNumber;
                    if (telemetry?.lat != null && telemetry?.lng != null) {
                      pilotInfo.lat = telemetry.lat;
                      pilotInfo.lng = telemetry.lng;
                      pilotInfo.location = `${telemetry.lat.toFixed(4)}, ${telemetry.lng.toFixed(4)}`;
                    }
                  }
                }
              }

              // Build today's sectors from completed and in-flight legs
              const today = new Date().toISOString().split('T')[0];
              const legDate = leg.etd ? new Date(leg.etd).toISOString().split('T')[0] : null;
              
              if (legDate === today && (leg.status === 'COMPLETED' || leg.status === 'IN_FLIGHT')) {
                const departureTime = leg.atd || leg.actualEtd || leg.etd;
                const arrivalTime = leg.ata || leg.actualEta || leg.eta;
                
                let duration = 0;
                if (departureTime && arrivalTime) {
                  const dep = new Date(departureTime).getTime();
                  const arr = new Date(arrivalTime).getTime();
                  duration = (arr - dep) / (1000 * 60); // minutes
                } else if (leg.blockTimeMinutes) {
                  duration = leg.blockTimeMinutes;
                }

                // Try to get actual sector data from telemetry if available
                let actualDistance: number | undefined = leg.distance;
                if (tailNumber) {
                  try {
                    // Get telemetry history for this leg's time window
                    const legStart = departureTime ? new Date(departureTime) : new Date(leg.etd);
                    const legEnd = arrivalTime ? new Date(arrivalTime) : new Date(leg.eta);
                    const hoursBack = Math.max(1, Math.min(24, (Date.now() - legStart.getTime()) / (1000 * 60 * 60)));
                    
                    const history = await telemetryApi.getHistory(tailNumber, hoursBack, 500).catch(() => []);
                    
                    // Filter to leg time window
                    const legHistory = history.filter((log: any) => {
                      const logTime = new Date(log.recordedAt || log.timestamp);
                      return logTime >= legStart && logTime <= legEnd;
                    });
                    
                    // Calculate distance from telemetry if we have enough points
                    if (legHistory.length >= 2) {
                      let totalDistance = 0;
                      for (let i = 1; i < legHistory.length; i++) {
                        const prev = legHistory[i - 1];
                        const curr = legHistory[i];
                        if (prev.lat && prev.lng && curr.lat && curr.lng) {
                          // Haversine distance in nautical miles
                          const R = 3440.065; // Earth radius in nautical miles
                          const dLat = (curr.lat - prev.lat) * Math.PI / 180;
                          const dLng = (curr.lng - prev.lng) * Math.PI / 180;
                          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                                   Math.cos(prev.lat * Math.PI / 180) * Math.cos(curr.lat * Math.PI / 180) *
                                   Math.sin(dLng/2) * Math.sin(dLng/2);
                          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                          totalDistance += R * c;
                        }
                      }
                      if (totalDistance > 0) {
                        actualDistance = Math.round(totalDistance);
                      }
                    }
                  } catch (e) {
                    // Use leg distance if telemetry calculation fails
                  }
                }

                sectors.push({
                  from: leg.departureICAO || leg.from || 'N/A',
                  to: leg.arrivalICAO || leg.to || 'N/A',
                  departureTime: departureTime ? new Date(departureTime).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'N/A',
                  arrivalTime: arrivalTime ? new Date(arrivalTime).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'N/A',
                  duration: duration,
                  distance: actualDistance || leg.distance || undefined,
                });
              }
            }
          }

          // If no active assignment found, use last known location from missions
          if (pilotInfo.status === 'UNKNOWN' && lastKnownLocation) {
            pilotInfo.currentBase = lastKnownLocation.base;
            pilotInfo.status = 'STATIONARY';
            pilotInfo.aircraft = lastKnownLocation.aircraft;
            
            // Also check if we have current telemetry for this aircraft
            if (lastKnownLocation.aircraft) {
              const currentTelemetry = telemetrySnapshot[lastKnownLocation.aircraft];
              if (currentTelemetry) {
                const speed = currentTelemetry.groundSpeed || 0;
                if (speed > 30) {
                  pilotInfo.status = 'IN_FLIGHT';
                  pilotInfo.currentBase = `IN FLIGHT → ${lastKnownLocation.base}`;
                } else {
                  pilotInfo.status = 'STATIONARY';
                  pilotInfo.location = `${currentTelemetry.lat.toFixed(4)}, ${currentTelemetry.lng.toFixed(4)}`;
                  pilotInfo.lat = currentTelemetry.lat;
                  pilotInfo.lng = currentTelemetry.lng;
                }
              }
            }
          }
          
          // If still unknown, check all aircraft this pilot has been on for current telemetry
          if (pilotInfo.status === 'UNKNOWN' && pilotAircraft.size > 0) {
            // Check each aircraft's current telemetry
            for (const tailNumber of pilotAircraft) {
              const telemetry = telemetrySnapshot[tailNumber];
              const aircraftDetails = aircraftMap.get(tailNumber) as any;
              
              if (telemetry && telemetry.lat && telemetry.lng) {
                const speed = telemetry.groundSpeed || 0;
                
                // Determine base: prefer aircraft's lastLandedLocation, then lastKnownLocation from missions
                let base = aircraftDetails?.lastLandedLocation;
                if (!base && lastKnownLocation && lastKnownLocation.aircraft === tailNumber) {
                  base = lastKnownLocation.base;
                }
                
                if (base && base !== 'N/A') {
                  if (speed > 30) {
                    pilotInfo.currentBase = `IN FLIGHT → ${base}`;
                    pilotInfo.status = 'IN_FLIGHT';
                  } else {
                    pilotInfo.currentBase = base;
                    pilotInfo.status = 'STATIONARY';
                  }
                  pilotInfo.aircraft = tailNumber;
                  pilotInfo.location = `${telemetry.lat.toFixed(4)}, ${telemetry.lng.toFixed(4)}`;
                  pilotInfo.lat = telemetry.lat;
                  pilotInfo.lng = telemetry.lng;
                  break;
                }
              }
            }
          }

          return { pilotId, pilotInfo, sectors };
        });

        const pilotResults = await Promise.all(pilotPromises);

        pilotResults.forEach(({ pilotId, pilotInfo, sectors }) => {
          pilotData[pilotId] = pilotInfo;
          sectorsData[pilotId] = sectors.sort((a, b) => {
            const timeA = a.departureTime !== 'N/A' ? a.departureTime : '00:00';
            const timeB = b.departureTime !== 'N/A' ? b.departureTime : '00:00';
            return timeA.localeCompare(timeB);
          });
        });

        // v2Track Metadata: match pilot names from v2Track to our pilot registry
        // This fills in location for pilots that missions didn't cover
        const activeTails = Object.keys(telemetrySnapshot).filter(
          (t) => telemetrySnapshot[t]?.lat != null && telemetrySnapshot[t]?.lng != null
        );
        const metaPromises = activeTails.map((tailNumber) =>
          telemetryApi.getTripMetadata(tailNumber)
            .then((m) => ({ tailNumber, meta: m }))
            .catch(() => ({ tailNumber, meta: null }))
        );
        const metaResults = await Promise.all(metaPromises);

        // Build multiple lookup keys per pilot for fuzzy name matching
        // v2Track may return "Rahim Datoo" but our DB has firstName="Rahim" lastName="Munir Datoo"
        const nameToPilotId = new Map<string, string>();
        pilots.forEach((p) => {
          const first = ((p as any).firstName || '').replace(/\s+/g, ' ').trim().toLowerCase();
          const last = ((p as any).lastName || '').replace(/\s+/g, ' ').trim().toLowerCase();
          const full = `${first} ${last}`.trim();
          if (full) nameToPilotId.set(full, p.id);
          if (last) nameToPilotId.set(last, p.id);
          // Also index by each word in last name (e.g. "Munir Datoo" → "munir" and "datoo")
          if (last) {
            last.split(' ').forEach((word) => {
              if (word.length >= 3) nameToPilotId.set(word, p.id);
            });
          }
          // Index first+last-word combo (e.g. "rahim datoo")
          if (first && last) {
            const lastWord = last.split(' ').pop();
            if (lastWord) nameToPilotId.set(`${first} ${lastWord}`, p.id);
          }
        });

        for (const { tailNumber, meta } of metaResults) {
          if (!meta?.pilotName) continue;
          const v2Name = meta.pilotName.replace(/\s+/g, ' ').trim().toLowerCase();
          // Try exact full match, then last word of v2Track name, then each word
          let matchedPilotId = nameToPilotId.get(v2Name);
          if (!matchedPilotId) {
            const parts = v2Name.split(' ');
            const lastName = parts[parts.length - 1];
            if (lastName) matchedPilotId = nameToPilotId.get(lastName);
          }
          if (!matchedPilotId) {
            // Try first+last word combo (e.g. "rahim datoo" from "Rahim  Datoo")
            const parts = v2Name.split(' ');
            if (parts.length >= 2) {
              matchedPilotId = nameToPilotId.get(`${parts[0]} ${parts[parts.length - 1]}`);
            }
          }
          if (!matchedPilotId) continue;

          const existing = pilotData[matchedPilotId];
          // Only override if pilot currently has no location (UNKNOWN or N/A)
          if (existing && existing.status !== 'UNKNOWN' && existing.currentBase !== 'N/A') continue;

          const tel = telemetrySnapshot[tailNumber];
          if (!tel) continue;
          const speed = tel.groundSpeed || 0;
          const isFlying = speed > 30;
          const lastLoc = meta.lastLocation || meta.destination || tailNumber;

          pilotData[matchedPilotId] = {
            currentBase: isFlying ? `En route → ${meta.destination || lastLoc}` : (lastLoc || 'N/A'),
            status: isFlying ? 'IN_FLIGHT' : 'STATIONARY',
            aircraft: tailNumber,
            location: `${tel.lat.toFixed(4)}, ${tel.lng.toFixed(4)}`,
            lat: tel.lat,
            lng: tel.lng,
          };
        }

        if (isMounted) {
          setPilotTelemetry(pilotData);
          setPilotSectors(sectorsData);

          // Reverse-geocode v2Track positions for stationary pilots (latest place from v2Track)
          const toGeocode: Array<{ pilotId: string; lat: number; lng: number }> = [];
          const uniqueCoords = new Set<string>();
          for (const [pilotId, info] of Object.entries(pilotData)) {
            if (info.status === 'STATIONARY' && info.lat != null && info.lng != null) {
              toGeocode.push({ pilotId, lat: info.lat, lng: info.lng });
              uniqueCoords.add(`${Number(info.lat).toFixed(4)},${Number(info.lng).toFixed(4)}`);
            }
          }
          if (toGeocode.length > 0) {
            const coords = Array.from(uniqueCoords).map((k) => {
              const [lat, lng] = k.split(',').map(Number);
              return { lat, lng };
            });
            reverseGeocodeBatch(coords, Math.min(10, coords.length))
              .then((results) => {
                if (!isMounted) return;
                const placeNames: Record<string, string> = {};
                toGeocode.forEach(({ pilotId, lat, lng }) => {
                  const key = `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
                  const r = results[key];
                  if (r?.location && r.location !== `${lat.toFixed(4)}, ${lng.toFixed(4)}`) {
                    placeNames[pilotId] = r.location;
                  }
                });
                setPilotPlaceNames((prev) => ({ ...prev, ...placeNames }));
              })
              .catch(() => {});
          } else {
            setPilotPlaceNames({});
          }
        }
        }; // end run()

        await Promise.race([
          run(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Pilot telemetry timeout')), FETCH_TIMEOUT_MS)
          ),
        ]);
      } catch (error) {
        if (isMounted) {
          setPilotTelemetry({});
          setPilotSectors({});
        }
        console.warn('[PilotManager] Failed to fetch pilot telemetry:', error);
      } finally {
        if (isMounted) {
          setLoadingTelemetry(false);
        }
      }
    };

    fetchPilotTelemetry();
    // Refresh every 5 minutes (longer period for base location)
    const interval = setInterval(fetchPilotTelemetry, 5 * 60 * 1000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [pilots]);

  const filteredPilots = useMemo(() => {
    if (!Array.isArray(pilots)) return [];
    return pilots.filter(p => {
      const name = (p as any).name || `${(p as any).firstName || ''} ${(p as any).lastName || ''}`.trim() || (p as any).email || '';
      return name.toLowerCase().includes(filter.toLowerCase());
    });
  }, [pilots, filter]);

  // Calculate pilot metrics for the current month
  const getPilotActivity = (pilotId: string) => {
    const today = new Date().toISOString().split('T')[0];
    const missionsToday = quotes.filter(q => q.date === today && q.legs.some(l => l.assignedCrew?.includes(pilotId)));
    const totalTodayMins = missionsToday.reduce((sum, q) => sum + q.totalBlockTimeMinutes, 0);
    
    return {
      missionsToday: missionsToday.length,
      hoursToday: (totalTodayMins / 60).toFixed(1),
      isFlying: totalTodayMins > 0
    };
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in pb-24">
      <div className="flex justify-between items-end border-b border-slate-100 pb-8">
        <div>
          <h2 className="text-4xl font-black text-slate-900 tracking-tighter mb-2 uppercase">Pilot Registry</h2>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Crew Qualifications & Flight Duty Monitoring</p>
        </div>
        <div className="flex items-center gap-6">
           <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input 
                type="text" 
                placeholder="Search Pilot Identity..." 
                className="pl-12 pr-6 py-4 bg-white border border-slate-100 rounded-2xl text-xs font-black outline-none w-80 shadow-sm"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
           </div>
           <button className="px-8 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">Onboard Flight Crew</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPilots.map(pilot => {
          const activity = getPilotActivity(pilot.id);
          const frmsMetrics = frmsData[pilot.id];
          
          // Get profile picture URL
          const photoUrl = (pilot as any).photoUrl || (pilot as any).avatar;
          const proxyPhotoUrl = photoUrl && photoUrl.startsWith('http') 
            ? `${(import.meta as any).env?.VITE_API_URL || '/meos/api'}/auth/zoho-photo?url=${encodeURIComponent(photoUrl)}&jwt=${encodeURIComponent(localStorage.getItem('ma_jwt') || '')}`
            : photoUrl;
          
          // Get pilot name
          const pilotName = (pilot as any).name || `${(pilot as any).firstName || ''} ${(pilot as any).lastName || ''}`.trim() || (pilot as any).email || 'Unknown Pilot';
          const nameInitials = (() => {
            const spaceIndex = pilotName.indexOf(' ');
            return spaceIndex > 0 ? pilotName.charAt(spaceIndex + 1) : pilotName.charAt(0);
          })();
          
          // Get total flight hours from FRMS or fallback
          const totalFlightHours = frmsMetrics?.totalFlightHours ?? pilot.totalFlightHours ?? 0;
          
          // Get current base from v2Track telemetry (priority) - NEVER use FRMS fallback if we have v2Track data
          const telemetryInfo = pilotTelemetry[pilot.id];
          let currentBase = telemetryInfo?.currentBase;
          
          // Helper function to check if a value looks like an aircraft tail number (e.g., "5H-SRG")
          const isAircraftTailNumber = (value: string | null | undefined): boolean => {
            if (!value) return false;
            // Pattern: starts with digits/letters, has dash, ends with letters (e.g., "5H-SRG", "N123AB")
            return /^[A-Z0-9]+-[A-Z0-9]+$/i.test(value) || /^[A-Z0-9]{2,3}-[A-Z0-9]{2,3}$/i.test(value);
          };
          
          // Helper function to check if a value looks like a company name (e.g., "MIRACLE AIR")
          const isCompanyName = (value: string | null | undefined): boolean => {
            if (!value) return false;
            const companyNames = ['MIRACLE AIR', 'MIRACLE AIR TANZANIA', 'MIRACLE', 'MIRACLEAIR'];
            return companyNames.some(name => value.toUpperCase().includes(name));
          };
          
          // CRITICAL: If we have v2Track telemetry info (even if status is UNKNOWN), use it
          // This ensures we override FRMS fallback which might have "MIRACLE AIR"
          if (telemetryInfo) {
            // Use v2Track data, even if it's "N/A" - that's better than wrong FRMS data
            currentBase = telemetryInfo.currentBase || 'N/A';
          } else {
            // Only fall back to FRMS if we have NO v2Track data at all
            const frmsBase = frmsMetrics?.currentBase;
            const pilotLocation = pilot.currentLocation;
            
            // Filter out aircraft tail numbers AND company names
            if (frmsBase && !isAircraftTailNumber(frmsBase) && !isCompanyName(frmsBase)) {
              currentBase = frmsBase;
            } else if (pilotLocation && !isAircraftTailNumber(pilotLocation) && !isCompanyName(pilotLocation)) {
              currentBase = pilotLocation;
            } else {
              // If FRMS data is invalid (tail number or company name), show N/A instead
              currentBase = 'N/A';
            }
          }
          
          // Final check: if currentBase looks like an aircraft tail number or company name, set to N/A
          if (currentBase && (isAircraftTailNumber(currentBase) || isCompanyName(currentBase))) {
            currentBase = 'N/A';
          }
          
          // Ensure we always show something valid
          if (!currentBase || currentBase === 'N/A' || currentBase === '...') {
            currentBase = 'N/A';
          }
          
          // Get today's sectors from v2Track
          const todaySectors = pilotSectors[pilot.id] || [];
          
          // Get license expiry information from FRMS
          const licenseExpiry = frmsMetrics?.licenseExpiry;
          const medicalExpiry = frmsMetrics?.medicalExpiry;
          const irExpiry = frmsMetrics?.irExpiry;
          
          // Check which licenses are expired
          const now = new Date();
          const licenseExpired = licenseExpiry && new Date(licenseExpiry) < now;
          const medicalExpired = medicalExpiry && new Date(medicalExpiry) < now;
          const irExpired = irExpiry && new Date(irExpiry) < now;
          const hasExpiredLicense = licenseExpired || medicalExpired || irExpired;
          
          // Get expired license details for tooltip
          const expiredLicenses = [];
          if (licenseExpired) expiredLicenses.push(`License: ${new Date(licenseExpiry!).toLocaleDateString()}`);
          if (medicalExpired) expiredLicenses.push(`Medical: ${new Date(medicalExpiry!).toLocaleDateString()}`);
          if (irExpired) expiredLicenses.push(`IR: ${new Date(irExpiry!).toLocaleDateString()}`);
          
          // Get compliance status
          const complianceStatus = hasExpiredLicense ? 'EXPIRED' : 
                                  (licenseExpiry && new Date(licenseExpiry) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)) ? 'EXPIRING_SOON' : 'VALID';
          
          // Get duties for today and tomorrow
          const duties = frmsDuties[pilot.id] || { today: [], tomorrow: [] };
          const todayDuties = (duties.today || []) as any[];
          const tomorrowDuties = (duties.tomorrow || []) as any[];
          
          // Get today's duty type (primary duty type, or "OFF" if no duties)
          const todayDutyType = todayDuties.length > 0 
            ? (todayDuties[0].type || 'OFF').toUpperCase()
            : 'OFF';
          
          // Get tomorrow's duty type (primary duty type, or "OFF" if no duties)
          const tomorrowDutyType = tomorrowDuties.length > 0
            ? (tomorrowDuties[0].type || 'OFF').toUpperCase()
            : 'OFF';
          
          // Calculate duty hours today from actual duties, excluding TBD routes
          const dutyHoursToday = todayDuties.reduce((sum: number, d: any) => {
            // Don't count hours if route is TBD
            if (d.route === 'TBD' || d.route === 'tbd') {
              return sum;
            }
            return sum + (d.dutyDurationHours || d.displayHours || 0);
          }, 0);

          return (
            <div 
              key={pilot.id} 
              className={`rounded-[3rem] p-10 border shadow-sm hover:shadow-xl transition-all group overflow-hidden relative ${
                hasExpiredLicense 
                  ? 'bg-rose-50 border-rose-200 animate-pulse' 
                  : 'bg-white border-slate-100'
              }`}
              title={hasExpiredLicense ? `Expired Licenses:\n${expiredLicenses.join('\n')}` : ''}
            >
               <div className="flex justify-between items-start mb-10">
                  <div className="flex items-center gap-5">
                     <div className="w-16 h-16 bg-slate-900 rounded-3xl flex items-center justify-center text-white font-black text-xl shadow-lg uppercase group-hover:bg-indigo-600 transition-colors overflow-hidden relative">
                        {proxyPhotoUrl ? (
                          <img 
                            src={proxyPhotoUrl} 
                            alt={pilotName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.parentElement?.querySelector('.avatar-fallback') as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`avatar-fallback w-full h-full flex items-center justify-center ${proxyPhotoUrl ? 'hidden' : ''}`}>
                          {nameInitials}
                        </div>
                     </div>
                     <div>
                        <h4 className="text-xl font-black text-slate-900 leading-none mb-2">
                          {pilotName}
                        </h4>
                           <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                             {(() => {
                               const role = (pilot as any).role || pilot.role;
                               return role === 'CP' ? 'Chief Pilot' :
                                      role === 'DFO' ? 'Director Flight Ops' :
                                      role === 'PILOT' ? 'Pilot' :
                                      role === 'FIRST_OFFICER' ? 'First Officer' :
                                      role === 'FLIGHT_PLANNER' ? 'Flight Planner' :
                                      'Line Pilot';
                             })()}
                           </span>
                     </div>
                  </div>
                  <div className={`px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest ${
                     todayDutyType === 'FLIGHT' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                     todayDutyType === 'STANDBY' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                     todayDutyType === 'OFF' ? 'bg-slate-50 text-slate-400 border border-slate-100' :
                     'bg-slate-50 text-slate-400 border border-slate-100'
                  }`}>
                    DUTY: {todayDutyType}
                  </div>
               </div>

               <div className="grid grid-cols-2 gap-6 mb-10">
                  <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100">
                     <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Total TT</p>
                     <p className="text-xl font-black text-slate-800 font-mono tracking-tighter">
                       {loadingFrms ? '...' : `${Number(totalFlightHours).toLocaleString()}h`}
                     </p>
                  </div>
                  <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 relative overflow-hidden">
                     <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">
                       Current location <span className="text-slate-300 font-normal">· v2Track</span>
                     </p>
                     <div className="flex items-center gap-2 flex-wrap">
                        {telemetryInfo?.status === 'IN_FLIGHT' && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-[9px] font-bold text-emerald-600 uppercase">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En route
                          </span>
                        )}
                        {telemetryInfo?.status === 'STATIONARY' && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[9px] font-bold text-indigo-600 uppercase">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> Stationary
                          </span>
                        )}
                        <p className={`text-xl font-black tracking-tighter flex-1 min-w-0 ${
                          telemetryInfo?.status === 'IN_FLIGHT' ? 'text-emerald-600' :
                          telemetryInfo?.status === 'STATIONARY' ? 'text-indigo-600' :
                          'text-slate-600'
                        }`}>
                          {loadingTelemetry ? '…' : (
                            pilotPlaceNames[pilot.id]
                              ? pilotPlaceNames[pilot.id]
                              : (currentBase && currentBase !== 'N/A'
                                  ? currentBase.replace(/^IN FLIGHT → /i, 'En route → ')
                                  : 'N/A')
                          )}
                        </p>
                     </div>
                     {telemetryInfo?.aircraft && (
                       <p className="text-[8px] font-bold text-slate-500 uppercase mt-1">
                         Aircraft {telemetryInfo.aircraft}
                       </p>
                     )}
                     {telemetryInfo?.location && (
                       <p className="text-[7px] font-mono text-slate-400 mt-0.5" title="Position from v2Track">
                         {telemetryInfo.location}
                       </p>
                     )}
                  </div>
               </div>

               {/* Today's Duty */}
               <div className="mb-6 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex justify-between items-center">
                     <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Today's Duty</span>
                     <span className={`text-[11px] font-black uppercase tracking-widest ${
                        todayDutyType === 'FLIGHT' ? 'text-emerald-600' :
                        todayDutyType === 'STANDBY' ? 'text-amber-600' :
                        todayDutyType === 'OFF' ? 'text-slate-400' :
                        todayDutyType === 'TRAINING' ? 'text-indigo-600' :
                        'text-slate-600'
                     }`}>
                        {todayDutyType}
                     </span>
                  </div>
               </div>

               {/* Tomorrow's Duty */}
               <div className="mb-6 p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                  <div className="flex justify-between items-center">
                     <span className="text-[8px] font-black text-indigo-600 uppercase tracking-widest">Tomorrow's Duty</span>
                     <span className={`text-[11px] font-black uppercase tracking-widest ${
                        tomorrowDutyType === 'FLIGHT' ? 'text-emerald-600' :
                        tomorrowDutyType === 'STANDBY' ? 'text-amber-600' :
                        tomorrowDutyType === 'OFF' ? 'text-indigo-400' :
                        tomorrowDutyType === 'TRAINING' ? 'text-indigo-600' :
                        'text-indigo-700'
                     }`}>
                        {tomorrowDutyType}
                     </span>
                  </div>
               </div>

               <div className="space-y-4 pt-6 border-t border-slate-100">
                  {/* License Compliance with hover tooltip */}
                  <div className="flex justify-between items-center group/comp relative">
                     <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">License Compliance</span>
                     <div className="relative">
                        <span className={`text-[10px] font-black ${
                          complianceStatus === 'EXPIRED' ? 'text-rose-500' :
                          complianceStatus === 'EXPIRING_SOON' ? 'text-amber-500' :
                          'text-emerald-600'
                        }`}>
                          {complianceStatus === 'EXPIRED' ? 'EXPIRED' : 
                           complianceStatus === 'EXPIRING_SOON' ? 'EXPIRING SOON' : 
                           'ACTIVE'}
                        </span>
                        {hasExpiredLicense && (
                           <div className="absolute right-0 top-full mt-2 p-3 bg-slate-900 text-white text-[9px] rounded-xl shadow-xl opacity-0 group-hover/comp:opacity-100 transition-opacity pointer-events-none z-50 whitespace-pre-line min-w-[200px]">
                              <div className="font-black text-rose-400 mb-1 uppercase">Expired Licenses:</div>
                              {expiredLicenses.map((exp, idx) => (
                                 <div key={idx} className="text-slate-300">{exp}</div>
                              ))}
                           </div>
                        )}
                     </div>
                  </div>
                  
                  {/* Duty Hours Today (from FRMS duties, excluding TBD routes) - Only show if > 0 */}
                  {/* Today's Sectors from v2Track */}
                  <div className="space-y-2">
                     <div className="flex justify-between items-center">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Today's Sectors</span>
                        <span className="text-[10px] font-black text-indigo-600">
                           {todaySectors.length} Sector{todaySectors.length !== 1 ? 's' : ''}
                        </span>
                     </div>
                     {todaySectors.length > 0 ? (
                        <div className="space-y-1.5 max-h-32 overflow-y-auto custom-scrollbar">
                           {todaySectors.map((sector, idx) => (
                              <div key={idx} className="p-2 bg-indigo-50 rounded-lg border border-indigo-100">
                                 <div className="flex items-center justify-between mb-1">
                                    <span className="text-[9px] font-black text-indigo-700 uppercase">
                                       {sector.from} → {sector.to}
                                    </span>
                                    {sector.duration > 0 && (
                                       <span className="text-[8px] font-bold text-indigo-500">
                                          {Math.round(sector.duration)}min
                                       </span>
                                    )}
                                 </div>
                                 <div className="flex items-center gap-2 text-[8px] font-mono text-slate-600">
                                    <span>{sector.departureTime}</span>
                                    <span>→</span>
                                    <span>{sector.arrivalTime}</span>
                                    {sector.distance && (
                                       <>
                                          <span className="text-slate-400">•</span>
                                          <span>{Math.round(sector.distance)}nm</span>
                                       </>
                                    )}
                                 </div>
                              </div>
                           ))}
                        </div>
                     ) : (
                        <p className="text-[9px] font-bold text-slate-400 italic">
                           {loadingTelemetry ? 'Loading...' : 'No sectors today'}
                        </p>
                     )}
                  </div>
               </div>
               
               <div className="absolute top-0 right-0 w-32 h-32 -translate-y-16 translate-x-16 rounded-full bg-slate-50 opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PilotManager;
